import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { saveUploadcareImageToZola, fetchZolaMetadata } from 'actions/UploadcareActions';
import { getCachedMetadata } from 'selectors/uploadcareSelectors';
import UploadcareWidget from 'components/common/UploadcareWidget/UploadcareWidget';
import { isUploadcareEnabled } from 'components/common/UploadcareWidget/utils';
import ZolaImageUploader from './ZolaImageUploader';
import { fileUploaded } from '../../actions/fileActions';
import { getImageSizeRequirements } from '../../selectors/cardProjectSelector';
import featureFlags from '../../../util/featureFlags';

const minCropEnabled = featureFlags.get('uploadcareMinCrop');

const ImageUploader = ({
  copyImageToZola,
  inputId,
  imageElement,
  imageSizeRequirements,
  loadCache,
  onUploadComplete,
  renderButton,
  cachedMetadata,
  zolaFileUploaded,
}) => {
  const [isBusy, setIsBusy] = useState(false);
  const [cachedUrl, setCachedUrl] = useState(null);
  const contentKey = imageElement.uuid;

  useEffect(() => {
    if (!isUploadcareEnabled) return;

    const zolaImageId = (imageElement && imageElement.image_id) || null;
    const metadata = zolaImageId && cachedMetadata[zolaImageId];
    const url = (metadata && metadata.uploadcare_url) || null;

    // If the Zola image is set but its cache hasn't been loaded yet, fetch it.
    if (zolaImageId && !metadata) {
      loadCache(zolaImageId);
    }

    // To avoid useless rerenders, only update cachedUrl when it actually changes.
    if (cachedUrl !== url) {
      setCachedUrl(url);
    }
  }, [cachedMetadata, imageElement]);

  const saveUploadcareImage = uploadcareFile => {
    setIsBusy(true);
    copyImageToZola(uploadcareFile, cachedMetadata)
      .then(zolaFile => {
        setIsBusy(false);
        zolaFileUploaded(zolaFile, contentKey);
        onUploadComplete({ [contentKey]: zolaFile.uuid });
      })
      .catch(() => {
        setIsBusy(false);
      });
  };

  // Delay the initialization of the uploader if waiting for image size requirements.
  // This fixes an issue where Uploadcare doesn't invoke our callback after submitting an image.
  let cropRatio;

  if (minCropEnabled) {
    // If the FF is on, check the image size requirements
    const minCrop = useMemo(() => imageSizeRequirements.minCrops[contentKey], [
      imageSizeRequirements,
      contentKey,
    ]);
    cropRatio = minCrop ? `${minCrop.width}:${minCrop.height} minimum` : null;

    // Don't render anything if cropRatio isn't available yet
    if (!cropRatio) return null;
  } else {
    // If the FF is off, only fix the aspect ratio, not minimum dimensions
    cropRatio = `${imageElement.width}:${imageElement.height}`;
  }

  return isUploadcareEnabled ? (
    <UploadcareWidget
      cachedValue={cachedUrl}
      cropRatio={cropRatio}
      minWidth={imageSizeRequirements.minWidth}
      minHeight={imageSizeRequirements.minHeight}
      onUploadcareFileReady={saveUploadcareImage}
      isBusy={isBusy}
      renderButton={renderButton}
    />
  ) : (
    <ZolaImageUploader
      contentKey={contentKey}
      aspectRatio={imageElement.width / imageElement.height}
      minWidth={imageSizeRequirements.minWidth}
      minHeight={imageSizeRequirements.minHeight}
      onUploadComplete={onUploadComplete}
      inputId={inputId}
      renderButton={renderButton}
    />
  );
};

ImageUploader.propTypes = {
  copyImageToZola: PropTypes.func,
  imageElement: PropTypes.shape({}),
  onUploadComplete: PropTypes.func,
  zolaFileUploaded: PropTypes.func,
};

const mapStateToProps = state => ({
  cachedMetadata: getCachedMetadata(state),
  imageSizeRequirements: getImageSizeRequirements(state),
});

const mapDispatchToProps = dispatch => ({
  copyImageToZola: (uploadcareFile, cache) =>
    dispatch(saveUploadcareImageToZola(uploadcareFile, cache)),
  loadCache: zolaImageId => dispatch(fetchZolaMetadata(zolaImageId)),
  zolaFileUploaded: (zolaFile, contentKey) => dispatch(fileUploaded(zolaFile, contentKey)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ImageUploader);



// WEBPACK FOOTER //
// ./src/cards/components/common/ImageUploader.jsx